import { config } from "../Environment";

// Types
export interface UserFolder {
  _id: string;
  userId: string;
  name: string;
  color: string;
  icon?: string | null;
  sortOrder: number;
  count?: number;
  createdAt?: string;
  updatedAt?: string;
}

export interface ActivityMetadata {
  _id: string;
  userId: string;
  prescriptionId: string;
  folderId?: string | null;
  folderName?: string;
  folderColor?: string;
  tags: string[];
  color?: string | null;
  notes: string;
  sortOrder: number;
  isFavorite: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface BulkUpdateRequest {
  prescriptionIds: string[];
  operation: "moveToFolder" | "addTag" | "removeTag" | "favorite" | "unfavorite";
  value?: string | null;
}

// Type for the fetch function that will be passed in
type FetchFunction = (url: string, options?: RequestInit) => Promise<Response>;

// Folder API calls
export const folderApi = {

  /**
   * Get all folders for the current user
   */
  async getFolders(fetchFn: FetchFunction): Promise<UserFolder[]> {
    const response = await fetchFn(config.API_BASE + "/api/user-folders", {
      method: "GET",
      credentials: "include",
    });
    if (!response.ok) {
      throw new Error("Failed to fetch folders");
    }
    return response.json();
  },

  /**
   * Create a new folder
   */
  async createFolder(
    name: string,
    color: string,
    icon: string | undefined,
    fetchFn: FetchFunction
  ): Promise<UserFolder> {
    const response = await fetchFn(config.API_BASE + "/api/user-folders", {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ name, color, icon }),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || "Failed to create folder");
    }
    return response.json();
  },

  /**
   * Update a folder
   */
  async updateFolder(
    folderId: string,
    updates: { name?: string; color?: string; icon?: string },
    fetchFn: FetchFunction
  ): Promise<UserFolder> {
    const response = await fetchFn(
      config.API_BASE + `/api/user-folders/${folderId}`,
      {
        method: "PUT",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(updates),
      }
    );
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || "Failed to update folder");
    }
    return response.json();
  },

  /**
   * Delete a folder
   */
  async deleteFolder(folderId: string, fetchFn: FetchFunction): Promise<void> {
    const response = await fetchFn(
      config.API_BASE + `/api/user-folders/${folderId}`,
      {
        method: "DELETE",
        credentials: "include",
      }
    );
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || "Failed to delete folder");
    }
  },

  /**
   * Reorder folders
   */
  async reorderFolders(folderIds: string[], fetchFn: FetchFunction): Promise<void> {
    const response = await fetchFn(config.API_BASE + "/api/user-folders/reorder", {
      method: "PUT",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ folderIds }),
    });
    if (!response.ok) {
      throw new Error("Failed to reorder folders");
    }
  },
};

// Metadata API calls
export const metadataApi = {
  /**
   * Get all metadata for the current user
   */
  async getAllMetadata(fetchFn: FetchFunction): Promise<ActivityMetadata[]> {
    const response = await fetchFn(
      config.API_BASE + "/api/user-activity-metadata",
      {
        method: "GET",
        credentials: "include",
      }
    );
    if (!response.ok) {
      throw new Error("Failed to fetch metadata");
    }
    return response.json();
  },

  /**
   * Get metadata for a specific activity
   */
  async getMetadataForActivity(
    prescriptionId: string,
    fetchFn: FetchFunction
  ): Promise<ActivityMetadata | null> {
    const response = await fetchFn(
      config.API_BASE + `/api/user-activity-metadata/${prescriptionId}`,
      {
        method: "GET",
        credentials: "include",
      }
    );
    if (!response.ok) {
      throw new Error("Failed to fetch activity metadata");
    }
    return response.json();
  },

  /**
   * Move activity to a folder (or remove from folder if folderId is null)
   */
  async moveToFolder(
    prescriptionId: string,
    folderId: string | null,
    fetchFn: FetchFunction
  ): Promise<ActivityMetadata> {
    const response = await fetchFn(
      config.API_BASE + `/api/user-activity-metadata/${prescriptionId}/folder`,
      {
        method: "PUT",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ folderId }),
      }
    );
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || "Failed to move activity to folder");
    }
    return response.json();
  },

  /**
   * Toggle favorite status for an activity
   */
  async toggleFavorite(prescriptionId: string, fetchFn: FetchFunction): Promise<ActivityMetadata> {
    const response = await fetchFn(
      config.API_BASE +
        `/api/user-activity-metadata/${prescriptionId}/favorite`,
      {
        method: "PUT",
        credentials: "include",
      }
    );
    if (!response.ok) {
      let errorMessage = "Failed to toggle favorite";
      try {
        const error = await response.json();
        errorMessage = error.error || errorMessage;
      } catch (e) {
        // Response might not be JSON
        errorMessage = `Failed to toggle favorite (${response.status} ${response.statusText})`;
      }
      throw new Error(errorMessage);
    }
    return response.json();
  },

  /**
   * Update tags for an activity
   */
  async updateTags(
    prescriptionId: string,
    tags: string[],
    fetchFn: FetchFunction
  ): Promise<ActivityMetadata> {
    const response = await fetchFn(
      config.API_BASE + `/api/user-activity-metadata/${prescriptionId}/tags`,
      {
        method: "PUT",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ tags }),
      }
    );
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || "Failed to update tags");
    }
    return response.json();
  },

  /**
   * Update notes for an activity
   */
  async updateNotes(
    prescriptionId: string,
    notes: string,
    fetchFn: FetchFunction
  ): Promise<ActivityMetadata> {
    const response = await fetchFn(
      config.API_BASE + `/api/user-activity-metadata/${prescriptionId}/notes`,
      {
        method: "PUT",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ notes }),
      }
    );
    if (!response.ok) {
      throw new Error("Failed to update notes");
    }
    return response.json();
  },

  /**
   * Get all unique tags used by the current user
   */
  async getUserTags(fetchFn: FetchFunction): Promise<string[]> {
    const response = await fetchFn(config.API_BASE + "/api/user-tags", {
      method: "GET",
      credentials: "include",
    });
    if (!response.ok) {
      throw new Error("Failed to fetch tags");
    }
    return response.json();
  },

  /**
   * Perform bulk operations on multiple activities
   */
  async bulkUpdate(request: BulkUpdateRequest, fetchFn: FetchFunction): Promise<{
    message: string;
    modifiedCount: number;
  }> {
    const response = await fetchFn(
      config.API_BASE + "/api/user-activity-metadata/bulk",
      {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(request),
      }
    );
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || "Failed to perform bulk operation");
    }
    return response.json();
  },
};
